import { motion } from "framer-motion";
import {
    ArrowUpRight,
    BarChart3,
    CalendarDays,
    CheckCircle2,
    ChevronRight,
    FileText,
    Mail,
    Sparkles,
} from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const rise = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.6, ease: EASE, delay },
});

const ConceptTag = () => (
    <span className="absolute right-3 top-3 z-20 rounded-full border border-[#0F172A]/10 bg-white/90 px-2 py-0.5 font-mono-brand text-[8px] uppercase tracking-[0.2em] text-[#94A3B8]">
        Concept visual
    </span>
);

const RiskPanel = () => {
    const rows = [
        { id: "BO-1042", addr: "12 Oak Drive, Bristol", tag: "Overdue", when: "2 days", dot: "#EF4444", tagC: "text-[#EF4444] bg-[#EF4444]/10" },
        { id: "BO-1087", addr: "Riverside School", tag: "Missing paperwork", when: "1 day", dot: "#F59E0B", tagC: "text-[#B45309] bg-[#F59E0B]/10" },
        { id: "BO-1113", addr: "14 Market St, Leeds", tag: "Scheduling conflict", when: "Today", dot: "#EAB308", tagC: "text-[#A16207] bg-[#EAB308]/10" },
    ];
    return (
        <div className="relative flex h-72 flex-col rounded-2xl border border-[#0F172A]/[0.06] bg-gradient-to-b from-white to-[#F8FAFF] p-4" data-testid="panel-risk">
            <ConceptTag />
            <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-[#0F172A]">Jobs at Risk</p>
                <span className="flex items-center text-[10px] font-medium text-[#0066FF]">View all <ChevronRight className="h-3 w-3" /></span>
            </div>
            <div className="mt-3 space-y-2.5">
                {rows.map((r) => (
                    <div key={r.id} className="flex items-center gap-3 rounded-xl border border-[#0F172A]/[0.05] bg-white p-2.5">
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: r.dot }} />
                        <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-bold text-[#0F172A]">{r.id}</p>
                            <p className="truncate text-[10px] text-[#94A3B8]">{r.addr}</p>
                        </div>
                        <span className={`rounded-full px-2 py-0.5 text-[8px] font-semibold ${r.tagC}`}>{r.tag}</span>
                        <span className="w-9 text-right text-[9px] text-[#94A3B8]">{r.when}</span>
                    </div>
                ))}
            </div>
            <motion.div
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                className="mt-auto flex items-center gap-2.5 rounded-xl border border-[#0066FF]/15 bg-gradient-to-r from-[#F0F9FF] to-[#FAF5FF] p-3"
            >
                <Sparkles className="h-4 w-4 shrink-0 text-[#A855F7]" />
                <div>
                    <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#0066FF]">AI Alert</p>
                    <p className="text-[11px] text-[#334155]">3 jobs may be at risk of delay.</p>
                </div>
                <ChevronRight className="ml-auto h-4 w-4 text-[#94A3B8]" />
            </motion.div>
        </div>
    );
};

const RecommendPanel = () => (
    <div className="relative flex h-72 flex-col rounded-2xl border border-[#0F172A]/[0.06] bg-gradient-to-b from-[#FAF5FF] to-white p-4" data-testid="panel-recommend">
        <ConceptTag />
        <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#A855F7]" />
            <p className="text-sm font-bold text-[#0F172A]">AI Recommendation</p>
            <span className="ml-auto rounded-full bg-[#A855F7]/15 px-2 py-0.5 text-[9px] font-semibold text-[#A855F7]">Beta</span>
        </div>
        <div className="mt-3 rounded-xl border border-[#0F172A]/[0.06] bg-white p-3.5 shadow-sm">
            <div className="flex items-start gap-2.5">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#0066FF]/10">
                    <CalendarDays className="h-3.5 w-3.5 text-[#0066FF]" />
                </span>
                <div>
                    <p className="text-[11px] font-bold text-[#0F172A]">Reassign job to reduce travel time</p>
                    <p className="mt-0.5 text-[10px] leading-snug text-[#64748B]">Move BO-1087 to Sam (Leeds) to save 48 minutes and keep the day on track.</p>
                </div>
            </div>
            <div className="mt-3 flex items-center justify-between gap-2 text-center">
                <div className="flex-1 rounded-lg bg-[#F8FAFF] p-2">
                    <p className="text-[8px] uppercase tracking-wide text-[#94A3B8]">Current assignment</p>
                    <p className="mt-1 text-[10px] font-bold text-[#0F172A]">James Wilson</p>
                    <p className="text-[9px] text-[#94A3B8]">52 min drive</p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-[#94A3B8]" />
                <div className="flex-1 rounded-lg border border-[#0066FF]/20 bg-[#F0F9FF] p-2">
                    <p className="text-[8px] uppercase tracking-wide text-[#0066FF]">Suggested engineer</p>
                    <p className="mt-1 text-[10px] font-bold text-[#0F172A]">Sam Carter</p>
                    <p className="text-[9px] text-[#0066FF]">4 min drive</p>
                </div>
            </div>
        </div>
        <div className="mt-auto flex gap-2">
            <span className="flex-1 rounded-full border border-[#0F172A]/15 py-2 text-center text-[10px] font-semibold text-[#475569]">Keep current</span>
            <span className="flex-1 rounded-full bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] py-2 text-center text-[10px] font-semibold text-white shadow-md">Apply recommendation</span>
        </div>
    </div>
);

const AutomatePanel = () => {
    const items = [
        { icon: CheckCircle2, title: "Job completed", sub: "BO-1087 · Riverside School", time: "10:24", check: false },
        { icon: Mail, title: "Send customer update", sub: "Confirmation sent automatically", check: true },
        { icon: FileText, title: "Create invoice", sub: "Invoice drafted in Xero", check: true },
        { icon: BarChart3, title: "Update records", sub: "Job, assets and history updated", check: true },
    ];
    return (
        <div className="relative flex h-72 flex-col rounded-2xl border border-[#0F172A]/[0.06] bg-gradient-to-b from-white to-[#F8FAFF] p-4" data-testid="panel-automate">
            <ConceptTag />
            <div className="relative space-y-2.5">
                <div aria-hidden="true" className="absolute bottom-4 left-[19px] top-4 border-l border-dashed border-[#0066FF]/30" />
                {items.map((it, i) => {
                    const Icon = it.icon;
                    return (
                        <motion.div
                            key={it.title}
                            initial={{ opacity: 0, x: 12 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.45, ease: EASE, delay: 0.3 + i * 0.15 }}
                            className="relative flex items-center gap-3 rounded-xl border border-[#0F172A]/[0.05] bg-white p-2.5 shadow-sm"
                        >
                            <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${i === 0 ? "bg-gradient-to-br from-[#00E5FF] to-[#0066FF]" : "bg-[#0066FF]/10"}`}>
                                <Icon className={`h-3.5 w-3.5 ${i === 0 ? "text-white" : "text-[#0066FF]"}`} />
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="text-[11px] font-bold text-[#0F172A]">{it.title}</p>
                                <p className="truncate text-[9.5px] text-[#94A3B8]">{it.sub}</p>
                            </div>
                            {it.time && <span className="text-[9px] text-[#94A3B8]">{it.time}</span>}
                            {it.check && <CheckCircle2 className="h-4 w-4 shrink-0 text-[#0066FF]" />}
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
};

const ASK_TED = [
    { q: "“Which jobs are at risk this week?”", a: "3 jobs need attention — 2 overdue, 1 missing paperwork." },
    { q: "“Any invoices I should chase?”", a: "2 invoices are over 14 days old — worth following up today." },
    { q: "“What's running low in stock?”", a: "32mm fittings are below the reorder point — 4 left." },
    { q: "“Who's due a service reminder?”", a: "214 customers haven't rebooked in 12+ months — start a journey." },
];

const COLUMNS = [
    {
        num: "01",
        title: "Spot Problems Early",
        body: "AI highlights overdue jobs, scheduling conflicts and missing paperwork before they become bigger issues.",
        panel: <RiskPanel />,
    },
    {
        num: "02",
        title: "Recommend the Next Best Action",
        body: "AI-supported recommendations help your team prioritise work, reassign jobs and make better operational decisions.",
        panel: <RecommendPanel />,
    },
    {
        num: "03",
        title: "Automate the Everyday",
        body: "Connect job completion, customer updates and invoicing through intelligent automation that reduces admin.",
        panel: <AutomatePanel />,
    },
];

export default function AiAct() {
    return (
        <section
            className="relative bg-[#F6F9FE]"
            style={{
                backgroundImage:
                    "radial-gradient(circle at 8% 0%, rgba(0,229,255,0.05) 0%, transparent 40%), radial-gradient(circle at 95% 100%, rgba(168,85,247,0.05) 0%, transparent 40%), linear-gradient(to right, rgba(15,23,42,0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.03) 1px, transparent 1px)",
                backgroundSize: "auto, auto, 44px 44px, 44px 44px",
            }}
            data-testid="ai-act-section"
            aria-labelledby="ai-act-heading"
        >
            <div className="mx-auto max-w-6xl px-6 py-24 sm:py-32">
                <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                    <motion.div {...rise(0)}>
                        <span
                            className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/10 bg-white px-4 py-1.5 shadow-sm"
                            data-testid="ai-act-eyebrow"
                        >
                            <Sparkles className="h-3 w-3 text-[#A855F7]" />
                            <span className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#475569] sm:text-xs">
                                Intelligence built into every workflow
                            </span>
                        </span>
                        <h2
                            id="ai-act-heading"
                            className="mt-6 font-display text-4xl font-bold leading-[1.12] tracking-tight text-[#0F172A] sm:text-5xl"
                            data-testid="ai-act-heading"
                        >
                            AI That Doesn't Just Analyse.
                            <br />
                            <span className="text-brand-gradient">It Helps You Act.</span>
                        </h2>
                    </motion.div>
                    <motion.div {...rise(0.15)} className="flex max-w-sm flex-col items-start gap-6">
                        <p className="text-base leading-relaxed text-[#64748B]" data-testid="ai-act-intro">
                            TedOps AI helps teams spot issues sooner, recommend next steps and
                            automate routine work using real operational data.
                        </p>
                        <button
                            type="button"
                            className="group inline-flex items-center gap-3 rounded-full bg-[#0B1220] py-2 pl-6 pr-2 text-xs font-bold uppercase tracking-[0.12em] text-white transition-transform duration-300 hover:scale-[1.03]"
                            data-testid="see-ai-button"
                        >
                            See AI in Action
                            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#00E5FF] to-[#0066FF] transition-transform duration-300 group-hover:rotate-45">
                                <ArrowUpRight className="h-4 w-4" />
                            </span>
                        </button>
                    </motion.div>
                </div>

                <div className="mt-16 grid gap-6 lg:grid-cols-3">
                    {COLUMNS.map((col, i) => (
                        <motion.article
                            key={col.num}
                            {...rise(0.1 + i * 0.1)}
                            className="flex flex-col rounded-3xl border border-[#0F172A]/[0.07] bg-white p-5 shadow-[0_2px_12px_-4px_rgba(11,18,32,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_45px_-20px_rgba(11,18,32,0.18)]"
                            data-testid={`ai-column-${i + 1}`}
                        >
                            {col.panel}
                            <p className="mt-7 font-mono-brand text-xs font-medium tracking-[0.25em] text-[#0066FF]">{col.num}</p>
                            <h3 className="mt-3 font-display text-xl font-bold leading-snug text-[#0F172A]">{col.title}</h3>
                            <p className="mt-3 text-[15px] leading-relaxed text-[#64748B]">{col.body}</p>
                        </motion.article>
                    ))}
                </div>

                <motion.div {...rise(0.2)} className="mt-16" data-testid="ask-ted-row">
                    <p className="font-mono-brand text-[10px] font-medium uppercase tracking-[0.3em] text-[#A855F7]">
                        Ask Ted — examples from live data
                    </p>
                    <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-[#0F172A] sm:text-3xl">
                        Ask questions. Get answers you can act on.
                    </h3>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {ASK_TED.map((ex, i) => (
                            <motion.div
                                key={ex.q}
                                {...rise(0.1 + i * 0.06)}
                                className="rounded-2xl border border-[#0F172A]/[0.07] bg-white p-5 shadow-[0_2px_12px_-4px_rgba(11,18,32,0.06)]"
                                data-testid={`ask-ted-${i + 1}`}
                            >
                                <Sparkles className="h-4 w-4 text-[#A855F7]" />
                                <p className="mt-3 text-sm font-bold leading-snug text-[#0F172A]">{ex.q}</p>
                                <p className="mt-2 text-[13px] leading-relaxed text-[#64748B]">{ex.a}</p>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
